import { useState, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  PlusIcon,
  PencilIcon,
  MagnifyingGlassIcon,
  ArrowPathIcon,
  ExclamationTriangleIcon,
  BuildingOffice2Icon,
} from '@heroicons/react/24/outline';
import Card, { CardBody } from '@/components/ui/Card';
import Button from '@/components/ui/Button';
import Modal, { ModalBody, ModalFooter } from '@/components/ui/Modal';
import Input from '@/components/ui/Input';
import Spinner from '@/components/ui/Spinner';
import EmptyState from '@/components/ui/EmptyState';
import {
  TableContainer, Table, TableHead, TableBody, TableRow, TableHeaderCell, TableCell,
} from '@/components/ui/Table';
import { useApi } from '@/hooks/useApi';
import type { OrganizationsListResponse } from '@/types/api';

export default function AdminOrganizationsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [search, setSearch] = useState(searchParams.get('q') || '');
  const [debouncedSearch, setDebouncedSearch] = useState(search);
  const [showModal, setShowModal] = useState(false);

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
    }, 300);
    return () => clearTimeout(timer);
  }, [search]);

  // Build API endpoint with query params
  const apiEndpoint = useMemo(() => {
    const params = new URLSearchParams();
    if (debouncedSearch) params.set('q', debouncedSearch);
    params.set('pageSize', '50');
    const queryString = params.toString();
    return `admin/organizations${queryString ? `?${queryString}` : ''}`;
  }, [debouncedSearch]);

  // Fetch organizations from API
  const { data, isLoading, error, refetch } = useApi<OrganizationsListResponse>(apiEndpoint);

  // Update URL search params when filters change
  useEffect(() => {
    const params = new URLSearchParams();
    if (debouncedSearch) params.set('q', debouncedSearch);
    setSearchParams(params, { replace: true });
  }, [debouncedSearch, setSearchParams]);

  const organizations = data?.items || [];
  const total = data?.total || 0;

  // Calculate stats from actual data
  const stats = useMemo(() => {
    const totalPublishers = organizations.reduce((sum, o) => sum + (o.publishers_count || 0), 0);
    const totalUsers = organizations.reduce((sum, o) => sum + (o.users_count || 0), 0);
    return {
      total,
      totalPublishers,
      totalUsers,
    };
  }, [organizations, total]);

  // Format date for display
  const formatDate = (dateStr: string | null | undefined) => {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-slate-100">Organizations</h1>
          <p className="text-slate-600 dark:text-slate-300 mt-1">
            Manage organizations and their settings
            {total > 0 && <span className="ml-2 text-slate-500 dark:text-slate-400">({total} organizations)</span>}
          </p>
        </div>
        <div className="flex gap-3">
          <Button
            variant="outline"
            leftIcon={<ArrowPathIcon className="w-4 h-4" />}
            onClick={() => refetch()}
            disabled={isLoading}
          >
            Refresh
          </Button>
          <Button leftIcon={<PlusIcon className="w-4 h-4" />} onClick={() => setShowModal(true)}>
            Add Organization
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardBody className="text-center">
            <p className="text-3xl font-bold text-slate-900 dark:text-slate-100">{stats.total}</p>
            <p className="text-sm text-slate-600 dark:text-slate-300">Total Organizations</p>
          </CardBody>
        </Card>
        <Card>
          <CardBody className="text-center">
            <p className="text-3xl font-bold text-primary-500">{stats.totalPublishers}</p>
            <p className="text-sm text-slate-600 dark:text-slate-300">Total Publishers</p>
          </CardBody>
        </Card>
        <Card>
          <CardBody className="text-center">
            <p className="text-3xl font-bold text-secondary-500">{stats.totalUsers}</p>
            <p className="text-sm text-slate-600 dark:text-slate-300">Total Users</p>
          </CardBody>
        </Card>
      </div>

      {/* Search */}
      <Card padding="md">
        <div className="flex flex-col lg:flex-row gap-4">
          <div className="flex-1">
            <Input
              placeholder="Search organizations by name..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              leftIcon={<MagnifyingGlassIcon className="w-5 h-5" />}
            />
          </div>
        </div>
      </Card>

      {/* Error State */}
      {error && (
        <Card padding="lg">
          <div className="flex flex-col items-center justify-center py-8 text-center">
            <ExclamationTriangleIcon className="w-12 h-12 text-danger-500 mb-4" />
            <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100 mb-2">Failed to load organizations</h3>
            <p className="text-slate-600 dark:text-slate-300 mb-4">{error}</p>
            <Button onClick={() => refetch()} leftIcon={<ArrowPathIcon className="w-4 h-4" />}>
              Try Again
            </Button>
          </div>
        </Card>
      )}

      {/* Loading State */}
      {isLoading && !data && (
        <Card padding="lg">
          <div className="flex flex-col items-center justify-center py-12">
            <Spinner size="lg" />
            <p className="text-slate-600 dark:text-slate-300 mt-4">Loading organizations...</p>
          </div>
        </Card>
      )}

      {/* Empty State */}
      {!isLoading && !error && organizations.length === 0 && (
        <EmptyState
          icon={<BuildingOffice2Icon className="w-12 h-12" />}
          title="No organizations found"
          description={
            debouncedSearch
              ? 'Try adjusting your search'
              : 'Get started by adding your first organization'
          }
          action={
            debouncedSearch ? (
              <Button variant="outline" onClick={() => setSearch('')}>
                Clear Search
              </Button>
            ) : (
              <Button
                leftIcon={<PlusIcon className="w-4 h-4" />}
                onClick={() => setShowModal(true)}
              >
                Add Organization
              </Button>
            )
          }
        />
      )}

      {/* Organizations Table */}
      {!error && organizations.length > 0 && (
        <>
          <Card padding="none">
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow isHoverable={false}>
                    <TableHeaderCell>Organization</TableHeaderCell>
                    <TableHeaderCell>External Ref</TableHeaderCell>
                    <TableHeaderCell>Publishers</TableHeaderCell>
                    <TableHeaderCell>Users</TableHeaderCell>
                    <TableHeaderCell>Created</TableHeaderCell>
                    <TableHeaderCell>Actions</TableHeaderCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {organizations.map((org) => (
                    <TableRow key={org.id}>
                      <TableCell className="font-medium text-slate-900 dark:text-slate-100">{org.name}</TableCell>
                      <TableCell className="text-sm text-slate-600 dark:text-slate-300 font-mono">
                        {org.external_ref || '—'}
                      </TableCell>
                      <TableCell>{org.publishers_count ?? '—'}</TableCell>
                      <TableCell>{org.users_count ?? '—'}</TableCell>
                      <TableCell className="text-sm text-slate-600 dark:text-slate-300">
                        {formatDate(org.created_at)}
                      </TableCell>
                      <TableCell>
                        <Button variant="ghost" size="sm" leftIcon={<PencilIcon className="w-3 h-3" />}>
                          Edit
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Card>

          {/* Footer Info */}
          <div className="flex items-center justify-between">
            <p className="text-sm text-slate-600 dark:text-slate-300">
              Showing {organizations.length} of {total} organizations
              {isLoading && <span className="ml-2">(refreshing...)</span>}
            </p>
            <Button variant="outline" size="sm">
              Export
            </Button>
          </div>
        </>
      )}

      {/* Add Organization Modal */}
      <Modal isOpen={showModal} onClose={() => setShowModal(false)} title="Add Organization">
        <ModalBody>
          <Input label="Organization Name" placeholder="Enter organization name" />
        </ModalBody>
        <ModalFooter>
          <Button variant="outline" onClick={() => setShowModal(false)}>Cancel</Button>
          <Button onClick={() => setShowModal(false)}>Create</Button>
        </ModalFooter>
      </Modal>
    </div>
  );
}

